import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { ShareListUser } from '../users/types';
import { toShareListUsers } from '../users/utils';
import { attachId, attachIds } from '../utils';

import { NotesCategory } from './types';

export const notesApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getNotesUsers: build.query<CognitoAPIResponse<ShareListUser[]>, {
      subject: string,
      category?: string,
      noteKey?: string
    }>({
      query(arg) {
        const { subject, category, noteKey } = arg;

        return {
          url: 'notes/users',
          method: 'GET',
          params: {
            subject, category, noteKey,
          },
        };
      },
      providesTags: (result, error, arg) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'NotesPermissions' as const, id })), {
          type: 'NotesPermissions', id: `${arg.subject}#${arg.category}#${arg.noteKey}`,
        }]
        : [{
          type: 'NotesPermissions', id: `${arg.subject}#${arg.category}#${arg.noteKey}`,
        }]),
      transformResponse(response: any) {
        return toShareListUsers(response);
      },
    }),
    shareNotes: build.mutation<any, {
      userId: string,
      subject: string,
      category?: string,
      noteKey?: string
    } >({
      query(arg) {
        const { userId, subject, category, noteKey } = arg;

        return {
          url: 'notes/share',
          body: {
            userId, subject, category, noteKey,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesPermissions',
        id: arg.userId,
      }, {
        type: 'NotesPermissions',
        id: `${arg.subject}#${arg.category}#${arg.noteKey}`,
      }],
    }),
    unshareNotes: build.mutation<any, {
      userId: string,
      subject: string,
      category?: string,
      noteKey?: string
    } >({
      query(arg) {
        const { userId, subject, category, noteKey } = arg;

        return {
          url: 'notes/unshare',
          body: {
            userId, subject, category, noteKey,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesPermissions',
        id: arg.userId,
      }, {
        type: 'NotesPermissions',
        id: `${arg.subject}#${arg.category}#${arg.noteKey}`,
      }],
    }),
    getNotesCategory: build.query<CognitoAPIResponse<NotesCategory>,
    { subject: string, category: string }>({
      query(arg) {
        const { subject, category } = arg;
        return {
          url: `notes/category/${subject}/${category}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => `${entry.subject}#${entry.category}`);
      },
    }),
    getNotesCategories: build.query <CognitoAPIResponse<NotesCategory[]>, { subject: string }>({
      query(arg) {
        const { subject } = arg;
        return {
          url: `notes/category/${subject}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg?.subject,
      }],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => `${entry.subject}#${entry.category}`);
      },
    }),
    newNotesCategory: build.mutation<any, { subject: string, attributes: any }>({
      query(arg) {
        const { subject, attributes } = arg;

        return {
          url: 'notes/category/new',
          body: { ...attributes, subject },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }],
    }),
    newNotes: build.mutation<any, { subject: string, category: string, attributes: any }>({
      query(arg) {
        const { subject, category, attributes } = arg;
        return {
          url: 'notes/new',
          body: {
            subject, category, ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }, 'Notes'],
    }),
    deleteNotesCategory: build.mutation<any, { subject: string, category: string }>({
      query(arg) {
        const { subject, category } = arg;

        return {
          url: `notes/category/${subject}/${category}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    deleteNotes: build.mutation<any, {
      subject: string,
      category: string,
      noteId: string,
    }>({
      query(arg) {
        const { subject, noteId } = arg;
        return {
          url: `notes/${subject}/${encodeURIComponent(noteId)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }, {
        type: 'Notes',
        id: arg.noteId,
      }],
    }),
    updateNotesCategory:
    build.mutation<any, { subject: string, category: string, attributes: any }>({
      query(arg) {
        const { subject, category, attributes } = arg;
        return {
          url: `notes/category/${subject}/${category}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }],
    }),
    updateNotes: build.mutation<any,
    { subject: string, category: string, noteId: string, attributes: any }>({
      query(arg) {
        const { subject, noteId, attributes } = arg;
        return {
          url: `notes/${subject}/${encodeURIComponent(noteId)}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'NotesCategories',
        id: arg.subject,
      }, {
        type: 'Notes',
        id: arg.noteId,
      }],
    }),
  }),
});

export const {
  useDeleteNotesMutation,
  useDeleteNotesCategoryMutation,
  useNewNotesCategoryMutation,
  useNewNotesMutation,
  useGetNotesCategoriesQuery,
  useGetNotesCategoryQuery,
  useUpdateNotesCategoryMutation,
  useUpdateNotesMutation,
  useGetNotesUsersQuery,
  useShareNotesMutation,
  useUnshareNotesMutation,
} = notesApi;
